import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Lang } from "@/lib/i18n";

/**
 * Story DTO surfaced to the public archive (§5).
 * NEVER include author_contact here — admin-only, excluded from every read.
 */
export type StoryListItem = {
  id: string;
  slug: string;
  title: string | null;
  language: string;
  author_display: string | null;
  category_ids: string[];
  tags: unknown;
  content_warnings: string[] | null;
  published_at: string | null;
};

export type StoryFull = StoryListItem & {
  title_original: string | null;
  body: string | null;
  canonical_id: string | null;
};

export type ReactionTotals = {
  same_happened_to_me: number;
  support: number;
  thank_you: number;
};

const LIST_COLS =
  "id, slug, title, language, author_display, category_ids, tags, content_warnings, published_at";
const FULL_COLS =
  "id, slug, title, title_original, body, language, author_display, category_ids, tags, content_warnings, canonical_id, published_at";

export const storiesListQuery = (lang: Lang) =>
  queryOptions({
    queryKey: ["stories", "list", lang],
    queryFn: async (): Promise<StoryListItem[]> => {
      const { data, error } = await supabase
        .from("stories")
        .select(LIST_COLS)
        .eq("status", "published")
        .eq("language", lang)
        .order("published_at", { ascending: false })
        .limit(100);
      if (error) throw new Error(error.message);
      return (data ?? []) as StoryListItem[];
    },
  });

export const storyBySlugQuery = (slug: string) =>
  queryOptions({
    queryKey: ["stories", "slug", slug],
    queryFn: async (): Promise<StoryFull | null> => {
      const { data, error } = await supabase
        .from("stories")
        .select(FULL_COLS)
        .eq("status", "published")
        .eq("slug", slug)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return (data as StoryFull | null) ?? null;
    },
  });

export const reactionsTotalsQuery = (storyId: string) =>
  queryOptions({
    queryKey: ["reactions", "totals", storyId],
    queryFn: async (): Promise<ReactionTotals> => {
      const { data, error } = await supabase
        .from("reactions")
        .select("type")
        .eq("story_id", storyId);
      if (error) throw new Error(error.message);
      const totals: ReactionTotals = { same_happened_to_me: 0, support: 0, thank_you: 0 };
      for (const row of data ?? []) {
        const t = (row as { type: keyof ReactionTotals }).type;
        if (t in totals) totals[t] += 1;
      }
      return totals;
    },
  });

export const categoryBySlugQuery = (slug: string) =>
  queryOptions({
    queryKey: ["categories", "slug", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("categories")
        .select("id, slug")
        .eq("slug", slug)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data as { id: string; slug: string } | null;
    },
  });

export const categoriesAllQuery = () =>
  queryOptions({
    queryKey: ["categories", "all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("categories")
        .select("id, slug")
        .order("sort_order", { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as { id: string; slug: string }[];
    },
    staleTime: 5 * 60_000,
  });

export const storiesByCategoryQuery = (categoryId: string, lang: Lang) =>
  queryOptions({
    queryKey: ["stories", "category", categoryId, lang],
    queryFn: async (): Promise<StoryListItem[]> => {
      const { data, error } = await supabase
        .from("stories")
        .select(LIST_COLS)
        .eq("status", "published")
        .eq("language", lang)
        .contains("category_ids", [categoryId])
        .order("published_at", { ascending: false })
        .limit(100);
      if (error) throw new Error(error.message);
      return (data ?? []) as StoryListItem[];
    },
  });

export function formatLogDate(iso: string | null, lang: Lang): string {
  if (!iso) return "";
  const d = new Date(iso);
  // Ship's logbook style: YYYY-MM-DD UTC; locale-neutral by design.
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  void lang;
  return `${y}-${m}-${day}`;
}

export function categoryLabel(slug: string, lang: Lang, fallback: string): string {
  void lang;
  return fallback || slug;
}